import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { writeDispositionsCache, clearDispositionsCache } from '../../utils/dispositionsCache';
import axios from 'axios';
import API_ENDPOINTS from '../../api/endpoints';
import { gaLoginSuccess, gaLoginFailed, gaLogout } from '../../utils/analytics';

// Async thunk for login
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_ENDPOINTS.loginUser, {
        userCode: username,
        userPassword: password,
      });

      if (response.data?.response?.[0]?.status) {
        const userData = response.data.response[0].data;
        localStorage.setItem('token', userData.token);
        localStorage.setItem('user', JSON.stringify(userData));
        gaLoginSuccess(userData.userCode, userData.userRole || (userData.userIsAdmin ? 'admin' : 'advisor'));
        // Warm the static dispositions cache in the background so the first
        // Call Disposition modal opens instantly. Best-effort: login never waits
        // on it, and the modal falls back to the network if it is not ready.
        axios
          .get(API_ENDPOINTS.getDispositionsByOrg, { headers: { 'x-api-key': userData.token } })
          .then((dRes) => writeDispositionsCache(userData.userOrgId, dRes?.data?.response?.[0]?.data || []))
          .catch(() => {});
        return userData;
      } else {
        const msg = response.data?.response?.[0]?.message || 'Login failed';
        gaLoginFailed(username, msg);
        return rejectWithValue(msg);
      }
    } catch (error) {
      const msg = error.response?.data?.response?.[0]?.message || 'Login failed. Please try again.';
      gaLoginFailed(username, msg);
      return rejectWithValue(msg);
    }
  }
);

// Async thunk for logout (logoutReason: Break | Washroom | Lunch | Meeting)
export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async ({ userCode, logoutReason }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const headers = { 'x-api-key': auth.user?.token };
      gaLogout(userCode, logoutReason);
      
      await axios.post(API_ENDPOINTS.logoutUser, { userCode, logoutReason }, { headers });
      
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      clearDispositionsCache();
      return null;
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      clearDispositionsCache();
      return null;
    }
  }
);

const SPOOF_ORIGINAL_KEY = 'spoof_original_user';
const SPOOF_ORIGINAL_TOKEN_KEY = 'spoof_original_token';

// Async thunk for superadmin spoof
export const startSpoof = createAsyncThunk(
  'auth/startSpoof',
  async ({ targetUserCode }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const headers = { 'x-api-key': auth.user?.token };
      const response = await axios.post(
        API_ENDPOINTS.spoofStart,
        { targetUserCode },
        { headers }
      );
      const d = response.data?.response?.[0];
      if (!d?.status || !d?.data) {
        return rejectWithValue(d?.message || 'Spoof failed');
      }
      const targetUser = d.data;
      // Store original for exit
      sessionStorage.setItem(SPOOF_ORIGINAL_KEY, JSON.stringify(auth.user));
      sessionStorage.setItem(SPOOF_ORIGINAL_TOKEN_KEY, auth.user?.token || '');
      return { targetUser };
    } catch (error) {
      const msg = error.response?.data?.response?.[0]?.message || 'Spoof failed';
      return rejectWithValue(msg);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    isAuthenticated: false,
    loading: false,
    error: null,
    spoofingAs: null, // { userName, userCode } when spoofing
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    restoreUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.spoofingAs = null;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      clearDispositionsCache();
      sessionStorage.removeItem(SPOOF_ORIGINAL_KEY);
      sessionStorage.removeItem(SPOOF_ORIGINAL_TOKEN_KEY);
    },
    exitSpoof: (state) => {
      const original = sessionStorage.getItem(SPOOF_ORIGINAL_KEY);
      const originalToken = sessionStorage.getItem(SPOOF_ORIGINAL_TOKEN_KEY);
      if (original) {
        try {
          const parsed = JSON.parse(original);
          const token = originalToken || parsed?.token;
          state.user = parsed;
          state.spoofingAs = null;
          sessionStorage.removeItem(SPOOF_ORIGINAL_KEY);
          sessionStorage.removeItem(SPOOF_ORIGINAL_TOKEN_KEY);
          if (token) {
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify({ ...parsed, token }));
          }
        } catch (e) {
          console.warn('Failed to restore original user from spoof', e);
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.spoofingAs = null;
      })
      .addCase(startSpoof.fulfilled, (state, action) => {
        const { targetUser } = action.payload;
        state.spoofingAs = { userName: targetUser.userName, userCode: targetUser.userCode };
        state.user = targetUser;
        state.error = null;
      })
      .addCase(startSpoof.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearError, restoreUser, logout, exitSpoof } = authSlice.actions;
export default authSlice.reducer;
